import React from 'react';
import { arrayOf, func, number, shape, string } from 'prop-types';
import { change, Field } from 'redux-form';
import {
  Button,
  Col,
  FormControl,
  OverlayTrigger,
  Panel,
  Row,
  Tooltip
} from 'react-bootstrap';
import FontAwesome from 'react-fontawesome';

import { SELECT_OPTION_SHAPE } from 'utils/customPropTypes';
import Select from 'components/common/Select';
import FieldWrapper from 'components/common/FieldWrapper';
import PanelNavControl from './PanelNavControl';

export default function CategoryDetailPanel({
  arrayRemove,
  categoryDetails,
  categoryIds,
  categoryOptions,
  dispatch,
  landingPageUrls,
  leadGenerationMethodOptions,
  leadGenerationMethods,
  partnerNetworks,
  previousPage,
  selectCategory,
  selectedCategory
}) {
  const findIdFor = (categoryDetail) =>
    categoryDetail && categoryDetail.category_id;

  const currentCategoryDetail = categoryDetails.find(
    (detail) => detail.category_id === selectedCategory.value
  );
  const currentCategoryDetailIndex = categoryDetails.indexOf(
    currentCategoryDetail
  );
  const prevCategory = categoryOptions.find(
    (option) =>
      option.value ===
      findIdFor(categoryDetails[currentCategoryDetailIndex - 1])
  );
  const nextCategory = categoryOptions.find(
    (option) =>
      option.value ===
      findIdFor(categoryDetails[currentCategoryDetailIndex + 1])
  );
  const idToRemove = categoryIds.filter(
    (id) => id !== currentCategoryDetail.category_id
  );

  const handleCategorySelect = (category) => () => selectCategory(category);
  const removeCategoryDetail = () =>
    arrayRemove(
      'partnerSubmissionForm',
      'category_details',
      currentCategoryDetailIndex
    );
  const updateCategoryIds = () =>
    dispatch(change('partnerSubmissionForm', 'category_ids', idToRemove));
  const selectNewCategoryOrPage = () => {
    if (prevCategory) {
      selectCategory(prevCategory);
    } else if (nextCategory) {
      selectCategory(nextCategory);
    } else {
      selectCategory({});
      previousPage();
    }
  };

  const handleCategoryDetailRemoveClick = () => {
    if (confirm('Delete vertical selection?')) {
      removeCategoryDetail();
      updateCategoryIds();
      selectNewCategoryOrPage();
    }
  };

  return (
    <Panel bsStyle="default">
      <Panel.Heading>
        <Row>
          <Col xs={6}>
            <h4 style={{ color: '#777777', marginTop: '10px' }}>
              Vertical: {selectedCategory.label}
            </h4>
          </Col>
          <Col xs={6}>
            <div className="pull-right">
              <PanelNavControl
                handleCategorySelect={handleCategorySelect}
                nextCategory={nextCategory}
                prevCategory={prevCategory}
              />
              <OverlayTrigger
                className="float-right"
                placement="bottom"
                overlay={<Tooltip id="tooltip">Remove Category</Tooltip>}
              >
                <Button
                  onClick={handleCategoryDetailRemoveClick}
                  bsStyle="link"
                  style={{ color: '#777777' }}
                >
                  <FontAwesome name="close" />
                </Button>
              </OverlayTrigger>
            </div>
          </Col>
        </Row>
      </Panel.Heading>
      <Panel.Body>
        <Row>
          <Col xs={12}>
            <fieldset className="with-legend">
              <legend>Current Monthly Call Volume</legend>
              <Row>
                <Col xs={6}>
                  <Field
                    component="input"
                    name="id"
                    style={{ display: 'none' }}
                  />
                  <Field
                    component={FieldWrapper}
                    hideLabelInError
                    inputComponent={FormControl}
                    inputComponentProps={{
                      autoFocus: true,
                      tabIndex: '1',
                      type: 'number'
                    }}
                    label="# of Raw Calls"
                    name={`category_details[${currentCategoryDetailIndex}].raw_call_count`}
                    required
                    showLabel
                    tooltip="The total number of calls that your campaigns generate for this vertical on a monthly basis"
                  />
                </Col>
                <Col xs={6}>
                  <Field
                    component={FieldWrapper}
                    hideLabelInError
                    inputComponent={FormControl}
                    inputComponentProps={{ tabIndex: '2', type: 'number' }}
                    label="# of Billable Calls"
                    name={`category_details[${currentCategoryDetailIndex}].billable_call_count`}
                    required
                    showLabel
                    tooltip="The total number of calls for which you received payment for your campaigns for this vertical on a monthly basis"
                  />
                </Col>
              </Row>
            </fieldset>
          </Col>
        </Row>
        <Row>
          <Col xs={6}>
            <Field
              addonContent="US $"
              component={FieldWrapper}
              hideLabelInError
              inputComponent={FormControl}
              inputComponentProps={{
                tabIndex: '3',
                type: 'number'
              }}
              label="Average Billable Call Payout"
              name={`category_details[${currentCategoryDetailIndex}].average_billable_call_payout`}
              required
              showAddon
              showLabel
              tooltip="The amount you received on average for each billable call"
            />
          </Col>
          <Col xs={6}>
            <Field
              component={FieldWrapper}
              hideLabelInError
              inputComponent={Select}
              inputComponentProps={{
                isCreatable: true,
                isMulti: true,
                noOptionsMessage: () => 'Enter method and press return to add',
                options: [
                  ...leadGenerationMethodOptions,
                  ...leadGenerationMethods.map((method) => ({
                    label: method,
                    value: method
                  }))
                ],
                placeholder: 'Select from list or add new',
                tabIndex: '4'
              }}
              label="Lead Generation Methods"
              name={`category_details[${currentCategoryDetailIndex}].lead_generation_methods`}
              required
              showLabel
              tooltip="The methods used to generate calls for your campaigns in this vertical"
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <Field
              component={FieldWrapper}
              hideLabelInError
              inputComponent={Select}
              inputComponentProps={{
                isCreatable: true,
                isMulti: true,
                noOptionsMessage: () => 'Enter network and press return to add',
                options: partnerNetworks.map((network) => ({
                  label: network,
                  value: network
                })),
                placeholder: '',
                tabIndex: '5'
              }}
              label="What other networks do you work with for this vertical?"
              name={`category_details[${currentCategoryDetailIndex}].partner_networks`}
              showLabel
              tooltip="What other networks are you currently using to monetize calls in this vertical"
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <Field
              component={FieldWrapper}
              hideLabelInError
              inputComponent={Select}
              inputComponentProps={{
                isCreatable: true,
                isMulti: true,
                noOptionsMessage: () => 'Enter URL and press return to add',
                options: landingPageUrls.map((url) => ({
                  label: url,
                  value: url
                })),
                placeholder: '',
                tabIndex: '6'
              }}
              label="Landing Page URLs"
              name={`category_details[${currentCategoryDetailIndex}].landing_page_urls`}
              showLabel
              tooltip="Please identify the landing page URLs used for your campaigns in this vertical"
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <Field
              component={FieldWrapper}
              hideLabelInError
              inputComponent="textarea"
              inputComponentProps={{
                className: 'form-control',
                rows: 5,
                tabIndex: '7'
              }}
              label="Any additional information"
              name={`category_details[${currentCategoryDetailIndex}].additional_information`}
              showLabel
              tooltip={
                'Please provide any additional information that you think ' +
                'would help us better understand what you do, how you do it, ' +
                'and how you think we can potentially work together.'
              }
            />
          </Col>
        </Row>
      </Panel.Body>
    </Panel>
  );
}

CategoryDetailPanel.propTypes = {
  arrayRemove: func.isRequired,
  categoryDetails: arrayOf(shape({})).isRequired,
  categoryIds: arrayOf(number).isRequired,
  categoryOptions: arrayOf(SELECT_OPTION_SHAPE).isRequired,
  dispatch: func.isRequired,
  landingPageUrls: arrayOf(string),
  leadGenerationMethodOptions: arrayOf(SELECT_OPTION_SHAPE).isRequired,
  leadGenerationMethods: arrayOf(string),
  partnerNetworks: arrayOf(string),
  previousPage: func.isRequired,
  selectCategory: func.isRequired,
  selectedCategory: shape({}).isRequired
};

CategoryDetailPanel.defaultProps = {
  landingPageUrls: [],
  leadGenerationMethods: [],
  partnerNetworks: []
};
